import { useEffect, useMemo } from "react";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { twMerge } from "tailwind-merge";

import {
  CancelSubmitButtons,
  errorToast,
  Switch,
  ToolTip,
} from "~/common/components";
import Modal, { ModalProps } from "~/common/components/Modal";
import Input from "~/common/components/ui/Input";
import Select from "~/common/components/ui/select/Select";
import TextArea from "~/common/components/ui/Textarea";
import {
  INTEGRATION_STATUS,
  INTEGRATION_TYPE,
  TEAM_MEMBER_VALUES,
} from "~/common/constants";
import { invitationLinksQueryOptionsFactory } from "~/providers/factories/invitationLinksQueryOptionsFactory";
import {
  useCurrentTeamMemberContext,
  useListIntegrations,
} from "~/providers/hooks";
import { useCreateNewConsultationLink } from "~/providers/hooks/useCreateNewConsultationLink";
import { useGetSchedulingMethods } from "~/providers/hooks/useGetSchedulingMethods";
import { useUpdateConsultationLink } from "~/providers/hooks/useUpdateConsultationLink";
import { ROUTES } from "~/providers/router";
import { upsertConsultationLinkRequestSchema } from "~/providers/schemas/invitations";
import {
  ConsultationLinkResponse,
  UpsertConsultationLinkRequest,
} from "~/providers/types/invitations";
import { OrganizationProvidersResponse } from "~/providers/types/teamMembers";

interface UpsertConsultationLinkModalProps extends ModalProps {
  link?: ConsultationLinkResponse;
  providers: OrganizationProvidersResponse[];
}

export const UpsertConsultationLinkModal = ({
  link,
  onClose,
  show,
  providers,
  ...props
}: UpsertConsultationLinkModalProps) => {
  const isEditing = !!link;
  const teamMember = useCurrentTeamMemberContext();
  const organizationId =
    teamMember?.organization_team_members?.[0]?.organization_id;
  const { data: integrations } = useListIntegrations({ organizationId });
  const { data: schedulingMethod } = useGetSchedulingMethods(
    organizationId,
    !!organizationId,
  );
  const queryClient = useQueryClient();

  const providerOptions = providers.map((provider) => ({
    value: provider.id,
    label: provider.full_name,
  }));

  const defaultValues: UpsertConsultationLinkRequest = useMemo(
    () => ({
      organization_id: organizationId,
      link_id: link?.id,
      default_consultation_fee: link?.default_consultation_fee ?? 0,
      default_primary_provider_id: link?.default_primary_provider?.id ?? "",
      auto_promote: link?.auto_promote ?? true,
      scheduling_included: link?.scheduling_included ?? false,
      description: link?.description ?? null,
      label: link?.label ?? "",
    }),
    [link, organizationId],
  );

  const {
    handleSubmit,
    register,
    watch,
    reset,
    control,
    formState: { errors, isValid },
  } = useForm<UpsertConsultationLinkRequest>({
    defaultValues,
    resolver: zodResolver(upsertConsultationLinkRequestSchema),
  });

  useEffect(() => {
    if (show) {
      reset(defaultValues);
    }
  }, [show, reset, defaultValues]);

  const createNewConsultationLinkMutation = useCreateNewConsultationLink();
  const updateConsultationLinkMutation = useUpdateConsultationLink();

  const isPending =
    createNewConsultationLinkMutation.isPending ||
    updateConsultationLinkMutation.isPending;

  const isPaymentEnabled = !!(integrations ?? [])?.find(
    (i) =>
      i.type === INTEGRATION_TYPE.STRIPE &&
      i.status === INTEGRATION_STATUS.CONNECTED,
  );
  const isSchedulingEnabled =
    teamMember?.type === TEAM_MEMBER_VALUES.provider &&
    (!!schedulingMethod?.phone_number?.number ||
      !!schedulingMethod?.email ||
      !!schedulingMethod?.booking_link);

  const onSubmit: SubmitHandler<UpsertConsultationLinkRequest> = async (
    newLinkRequest,
  ) => {
    const payload = {
      ...newLinkRequest,
      default_primary_provider_id:
        newLinkRequest.default_primary_provider_id ?? null,
      scheduling_included:
        isSchedulingEnabled && newLinkRequest.scheduling_included,
      description: newLinkRequest.description ?? null,
    };
    const onSuccess = async () => {
      onClose();
      await queryClient.invalidateQueries({
        queryKey: invitationLinksQueryOptionsFactory.list._def,
      });
      toast.success(
        isEditing ? "Link updated successfully" : "Link created successfully",
      );
    };

    try {
      if (isEditing) {
        await updateConsultationLinkMutation.mutateAsync(payload);
      } else {
        await createNewConsultationLinkMutation.mutateAsync(payload);
      }
      await onSuccess();
    } catch (e) {
      errorToast(e);
    }
  };

  return (
    <Modal
      title={isEditing ? `Configure ${link.label}` : "Create new link"}
      show={show}
      onClose={onClose}
      className="max-h-[80vh] max-w-xl overflow-y-auto sm:w-4/5 md:w-2/3 xl:w-2/3"
      {...props}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <fieldset disabled={isPending} className="flex flex-col gap-4">
          <Input
            id="label"
            label="Label"
            required
            maxLength={100}
            charactersCount={watch("label")?.length}
            placeholder="Enter label"
            error={errors.label?.message}
            {...register("label")}
          />
          <TextArea
            id="description"
            label="Description"
            charactersCount={watch("description")?.length}
            maxCharacters={200}
            rows={4}
            error={errors.description?.message}
            placeholder="Enter description"
            className="placeholder:not-italic placeholder:text-gray-60"
            {...register("description")}
          />
          <Select
            id="default_primary_provider_id"
            label="Default primary provider"
            placeholder="Unassigned"
            options={[{ value: "", label: "Unassigned" }, ...providerOptions]}
            control={control}
            error={errors.default_primary_provider_id?.message}
            tooltip={
              <ToolTip
                className="bg-neutral-25 p-3.5"
                arrowClassName="bg-neutral-25"
                tooltip={
                  <div className="flex flex-col gap-2 text-gray-600">
                    <p className="font-semibold">Default primary provider</p>
                    <p>
                      All patients who initiate a consultation using this link
                      will automatically be assigned to the specified provider.
                    </p>
                    <p>
                      The Primary provider receives notifications regarding the
                      consultation.
                    </p>
                    <p>
                      For unassigned consultations, notifications will be sent
                      to the Notify list for unassigned consultations.
                    </p>
                  </div>
                }
              >
                <ExclamationCircleIcon className="h-5 w-5 rotate-180 stroke-2 text-gray-60" />
              </ToolTip>
            }
          />
          <Input
            id="fee"
            type="currency"
            disabled={!isPaymentEnabled}
            label="Default consultation fee"
            tooltip={
              <ToolTip
                className="bg-neutral-25 p-3.5"
                arrowClassName="bg-neutral-25"
                tooltip={
                  <div className="flex flex-col gap-2 text-gray-600">
                    <p className="font-semibold">Default consultation fee</p>
                    <p>
                      The default fee patients will pay to proceed with the
                      intake. You will have the opportunity to override this fee
                      when promoting each patient to intake.{" "}
                    </p>
                  </div>
                }
              >
                <ExclamationCircleIcon className="h-5 w-5 rotate-180 stroke-2 text-gray-60" />
              </ToolTip>
            }
            description={
              !isPaymentEnabled && (
                <p className="text-neutral-400">
                  To set a fee, enable the Stripe integration in{" "}
                  <Link
                    className="font-medium text-primary-600"
                    to={ROUTES.toolsIntegrations}
                  >
                    Integrations.
                  </Link>
                </p>
              )
            }
            placeholder="0"
            error={errors.default_consultation_fee?.message}
            {...register("default_consultation_fee", {
              setValueAs: (value) => Number(value),
            })}
          />
          <hr className="border-[1px] border-b" />
          <Switch
            id="auto_promote"
            containerClassName="w-full"
            className="flex-row-reverse justify-between text-sm font-medium"
            {...register("auto_promote")}
          >
            <div className="flex items-center gap-1.5">
              <span>Auto promote to intake</span>
              <ToolTip
                className="bg-neutral-25 p-3.5"
                arrowClassName="bg-neutral-25"
                tooltip={
                  <div className="flex flex-col gap-2 text-gray-600">
                    <p className="font-semibold">Auto promote to intake</p>
                    <div className="flex flex-col gap-4">
                      <p>
                        <span className="font-semibold">On: </span>Patients are
                        automatically promoted to intake upon submitting a
                        screening. This allows patients to complete the
                        screening and intake as one continuous flow. You will be
                        notified when a screening is submitted, but no action is
                        required to promote to intake.
                      </p>
                      <p>
                        <span className="font-semibold">Off: </span>You must
                        manually promote patients to intake to continue the
                        consultation. This gives you the opportunity to review
                        screening submissions to assess clinical fit before
                        proceeding.
                      </p>
                    </div>
                  </div>
                }
              >
                <ExclamationCircleIcon className="h-5 w-5 rotate-180 stroke-2 text-gray-60" />
              </ToolTip>
            </div>
          </Switch>
          <hr className="border-[1px] border-b" />
          <Switch
            id="scheduling_included"
            containerClassName="w-full"
            className={twMerge(
              "flex-row-reverse justify-between text-sm font-medium",
              !isSchedulingEnabled && "items-start",
            )}
            disabled={!isSchedulingEnabled}
            {...register("scheduling_included")}
          >
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5">
                <span>Scheduling included</span>
                <ToolTip
                  className="bg-neutral-25 p-3.5"
                  arrowClassName="bg-neutral-25"
                  tooltip={
                    <div className="flex flex-col gap-2 text-gray-600">
                      <p className="font-semibold">Scheduling included</p>
                      <div className="flex flex-col gap-4">
                        <p>
                          <span className="font-semibold">On: </span>Patients
                          are asked to schedule an appointment once they submit
                          the intake. The{" "}
                          <span className="font-semibold">
                            Appointment scheduling methods
                          </span>{" "}
                          defined in your settings will be presented as options
                          to the patient.
                        </p>
                        <p>
                          <span className="font-semibold">Off: </span>Patients
                          are not asked to schedule an appointment as part of
                          the consultation workflow.
                        </p>
                      </div>
                    </div>
                  }
                >
                  <ExclamationCircleIcon className="h-5 w-5 rotate-180 stroke-2 text-gray-60" />
                </ToolTip>
              </div>
              {!isSchedulingEnabled && (
                <p className="text-sm font-light text-neutral-400">
                  To include scheduling, add a method in{" "}
                  <Link
                    className="font-medium text-primary-600"
                    to={ROUTES.toolsGeneral}
                  >
                    Appointment scheduling
                  </Link>
                  .
                </p>
              )}
            </div>
          </Switch>
          <hr className="border-[1px] border-b" />
          <CancelSubmitButtons
            submitButtonLabel={isEditing ? "Save changes" : "Create link"}
            handleCancel={onClose}
            isSubmitting={isPending}
            containerClassName="w-auto ml-auto"
            disableSubmit={!isValid}
          />
        </fieldset>
      </form>
    </Modal>
  );
};
